import React, { useMemo, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Invoice } from '../models/table-models';
import { saveProtocolPicks } from '../utils/clients-hooks';
import { transformDateToString } from '../utils/helper-functions';
import { toastOptionsTopRight } from '../utils/Enums/ToastOptions';
import { showBackendError } from '../utils/backend-error';
import { CustomToastContent } from './CustomToastContent';
import { ProtocolPicker } from './ProtocolPicker';
import { useProtocolPicks } from '../utils/useProtocolPicks';

/**
 * Протоколите към документа: какво ще тръгне с мейла и възможността да се смени.
 *
 * Показва отметките такива, каквито ще се приложат при изпращане - минала ли е фактурата през избор,
 * това е самият избор; иначе са протоколите от периода ѝ, но само за обект с включено прикачване. Оттук
 * се вижда и се мени, без да се минава през диалога за обединяване: дотук отметките се задаваха само при
 * номериране и после нямаше откъде да се погледнат.
 */
export const DocumentProtocols = ({ invoices }: { invoices: Invoice[] }) => {
    const { t } = useTranslation('translation', { keyPrefix: 'invoice' });

    const ids = useMemo(() => invoices.map(({ id }) => id as number).filter(Boolean), [invoices]);
    const { protocolsByInvoice, loading, selectedProtocolIds, toggleProtocol, addProtocol, addedFor, picksFor } =
        useProtocolPicks(ids, ids.length > 0);
    const [saving, setSaving] = useState(false);

    if (ids.length === 0) return null;

    const save = async () => {
        setSaving(true);
        try {
            await saveProtocolPicks(picksFor(ids));
            toast.success(<CustomToastContent content={[t('protocolsSaved')]} />, toastOptionsTopRight);
        } catch (error) {
            showBackendError(error);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className='mt-4'>
            <h5>{t('protocolsForPeriod')}</h5>
            <p className='small text-muted mb-2'>{t('documentProtocolsHint')}</p>
            <table className='table table-sm table-bordered'>
                <thead>
                    <tr>
                        <th>{t('branch')}</th>
                        <th>{t('period')}</th>
                        <th>{t('protocolsForPeriod')}</th>
                    </tr>
                </thead>
                <tbody>
                    {invoices.map((invoice) => {
                        const row = protocolsByInvoice.get(invoice.id as number);
                        return (
                            <tr key={invoice.id}>
                                <td>{invoice.branchName}</td>
                                <td>
                                    {transformDateToString(invoice.fromDate)} - {transformDateToString(invoice.duePaymentDate)}
                                </td>
                                <td>
                                    <ProtocolPicker
                                        row={row}
                                        loading={loading}
                                        selectedProtocolIds={selectedProtocolIds}
                                        onToggleProtocol={toggleProtocol}
                                        added={addedFor(invoice.id as number)}
                                        onAddProtocol={(protocol) => addProtocol(invoice.id as number, protocol)}
                                    />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            <div className='d-flex justify-content-end'>
                {saving ? (
                    <Spinner animation='border' />
                ) : (
                    <Button variant='outline-success' size='sm' disabled={loading} onClick={save}>
                        {t('saveProtocols')}
                    </Button>
                )}
            </div>
        </div>
    );
};
